import { KOMPAS_DAYS, PILAR_LABEL } from '@iqrapath/shared'
import { Info } from 'lucide-react'
import { Link } from 'react-router-dom'
import { EmptyState } from '../../components/EmptyState.tsx'
import { Obj } from '../../components/Obj.tsx'
import { SkCard } from '../../components/Skeleton.tsx'
import { SourceLink } from '../../components/SourceLink.tsx'
import { Back } from './parts.tsx'
import { PILAR_OBJ, dayLong, useKompas } from './perjalanan.ts'

/**
 * /perjalanan/kompas — Kompas Diri (docs/PRD.md §6.12): four pillars from QS. Al-‘Asr, how active each was in the app over
 * the last 30 days, and one small step for the quietest. A mirror of activity, never a measure of faith.
 */
export function KompasPage() {
  const { data, isPending, isError, refetch } = useKompas()
  return (
    <div className="page pj">
      <Back />
      <h1>Kompas Diri</h1>
      <p className="muted" style={{ marginTop: -6 }}>Empat pilar yang disebut dalam QS. Al-‘Asr, dan seberapa sering kamu merawatnya di aplikasi ini selama {KOMPAS_DAYS} hari terakhir.</p>
      {isPending && <SkCard label="Memuat kompas" lines={6} style={{ marginTop: 14 }} />}
      {isError && (
        <EmptyState obj="kompas" title="Belum bisa dimuat" action={<button className="btn btn-primary" onClick={() => refetch()}>Coba lagi</button>}>
          Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.
        </EmptyState>
      )}
      {data && (
        <>
          <ul className="kp-grid">
            {data.pilar.map((p) => (
              <li key={p.pilar} className={`card kp-card reveal${p.pilar === data.lemah ? ' weak' : ''}`}>
                <Obj id={PILAR_OBJ[p.pilar]} size={44} />
                <b>{p.label}</b>
                <span className="kp-bar" role="meter" aria-label={`${p.label}: ${p.score} dari 100, ${p.tahap}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={p.score}>
                  <i style={{ width: `${Math.max(3, p.score)}%` }} />
                </span>
                <small><span className="num">{p.score}</span> · {p.tahap}</small>
              </li>
            ))}
          </ul>

          <section className="card pj-sec reveal" aria-labelledby="kp-saran-h">
            <h2 id="kp-saran-h">Satu langkah untuk {PILAR_LABEL[data.lemah]}</h2>
            {data.saran.kind === 'kebiasaan' && <p>Tambahkan kebiasaan “{data.saran.label}” lewat <Link to="/perjalanan">Atur kebiasaan</Link> di Perjalanan.</p>}
            {data.saran.kind === 'tantangan' && <p>Coba tantangan <Link to={`/perjalanan/tantangan/${data.saran.id}`}>{data.saran.judul}</Link>.</p>}
            {data.saran.kind === 'umum' && <p>Teruskan langkah kecil yang sudah berjalan.</p>}
          </section>

          <section className="card pj-sec reveal" aria-labelledby="kp-dasar-h">
            <h2 id="kp-dasar-h">Dasarnya</h2>
            {data.dasar.terjemah && <blockquote className="kp-quote">“{data.dasar.terjemah}”</blockquote>}
            <p className="pg-src"><SourceLink href={data.dasar.url}>{data.dasar.ref}, terjemahan Kemenag</SourceLink></p>
            <p className="help">Pengelompokan aktivitas ke tiap pilar adalah cara aplikasi ini merangkum: kebiasaan sesuai pilarnya, ayat harian ke Iman, niat yang selesai ke Amal saleh, kartu yang dibagikan ke Kebenaran, muhasabah ke Kesabaran, dan tantangan ke pilar programnya.</p>
          </section>

          <p className="pg-note" role="note"><Info size={16} aria-hidden="true" /> Kompas ini cermin kegiatanmu di aplikasi ({dayLong(data.from)}–{dayLong(data.to)}), bukan ukuran iman.</p>
        </>
      )}
    </div>
  )
}
